import dayjs from 'dayjs';
import { normalizeInviteBaseUrl } from './inviteBaseUrl';

/**
 * Calculate days until a future date or days since a past date
 */
export const calculateDaysUntil = (dateString: string): number => {
  const targetDate = dayjs(dateString).startOf('day');
  const today = dayjs().startOf('day');
  return targetDate.diff(today, 'day');
};

/**
 * Format event URL for guests to join (short branded format)
 */
export const formatEventUrl = (eventId: string, customUrl?: string): string => {
  if (customUrl) {
    return `${window.location.origin}/join/${customUrl}`;
  }
  return `${window.location.origin}/j/${eventId}`;
};

/**
 * The invite app's base URL. Every link to the invitation, and the editor's
 * preview frame, reads it from here — so a value that works for one works for
 * all of them.
 */
export const inviteBaseUrl = (): string => normalizeInviteBaseUrl(import.meta.env.VITE_INVITE_URL);

/**
 * Format invitation URL pointing to the invite app (for previewing the digital invitation)
 */
export const formatInvitationUrl = (eventId: string): string =>
  `${inviteBaseUrl()}/events/${eventId}`;

/**
 * URL of the read-only Heirloom demo invitation. Used by the upsell card
 * and pricing cards so couples on Blossom/Bouquet can preview what
 * Heirloom would deliver before paying €249. The slug is reserved
 * BE-side — see api/constants/demoEvent.mjs.
 *
 * @param lang Optional short language code (`en` | `el`). When provided,
 *   the BE returns translated demo content so the preview matches the
 *   couple's current dashboard language.
 */
export const formatDemoInvitationUrl = (lang?: 'en' | 'el'): string => {
  const qs = lang ? `?lang=${lang}` : '';
  return `${inviteBaseUrl()}/events/demo-heirloom${qs}`;
};

/**
 * Format a per-guest invitation URL with the RSVP code (and optionally the
 * event's join code) embedded as path segments rather than query strings.
 *
 * Shape: `/events/:eventId/r/:rsvpCode[/j/:joinCode]`
 *
 * Path segments over query strings is deliberate — `Referrer-Policy:
 * strict-origin-when-cross-origin` strips them on cross-origin requests,
 * URL-preview fetchers don't follow them, and analytics rarely log them.
 * Same rationale as `formatShareableUrl` and F-AUTH-09.
 *
 * Pass `joinCode` only when the couple has it enabled (`hasJoinCode === true`).
 * When omitted, the invite page still loads but the "Share your moments"
 * button won't prefill the app-side join form.
 */
export const formatGuestInvitationUrl = (
  eventId: string,
  rsvpCode: string,
  joinCode?: string,
): string => {
  const base = formatInvitationUrl(eventId);
  const rsvpSegment = `/r/${encodeURIComponent(rsvpCode)}`;
  const joinSegment = joinCode ? `/j/${encodeURIComponent(joinCode)}` : '';
  return `${base}${rsvpSegment}${joinSegment}`;
};

/**
 * Format a shareable event URL. When a join code is enabled, the code travels
 * as a path segment (`/j/{eventId}/c/{code}`) rather than a query string.
 * Path segments are stripped by `Referrer-Policy: strict-origin-when-cross-origin`,
 * less commonly logged by analytics tools, and not surfaced by URL-preview
 * fetchers the way query strings are. See F-AUTH-09.
 */
export const formatShareableUrl = (
  eventId: string,
  customUrl?: string,
  joinCode?: string,
  hasJoinCode?: boolean,
): string => {
  const baseUrl = formatEventUrl(eventId, customUrl);
  return hasJoinCode && joinCode ? `${baseUrl}/c/${encodeURIComponent(joinCode)}` : baseUrl;
};

/**
 * Shorten a shareable URL for *display only* — the full URL is still what we copy.
 * Drops the protocol and collapses the middle path so the host + final code stay
 * legible: `http://localhost:5005/j/fresh-cape-fskq/c/PNNSC64U` → `localhost:5005/…/PNNSC64U`.
 *
 * Presentational only — never feed this to copy/QR; those use the full URL.
 */
export const formatDisplayUrl = (url: string): string => {
  const noProtocol = url.replace(/^https?:\/\//, '');
  const [host, ...rest] = noProtocol.split('/');
  if (rest.length === 0) return host;
  const last = rest[rest.length - 1];
  return `${host}/…/${last}`;
};

/**
 * Copy text to clipboard with modern API
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
    } finally {
      textArea.remove();
    }
  }
};

/**
 * Format bytes to human-readable string
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};
